import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api } from './api'

/**
 * One business, the whole support picture: who is in it, where its credits went, what its sites
 * did, what it published, what the models cost, and what it was told in the chat. The screen a
 * ticket gets answered from.
 */
export default function TenantDetail({ id, onExpired }) {
  const { t, i18n } = useTranslation()
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)
  const [qty, setQty] = useState('')
  const [reason, setReason] = useState('')
  const [suspendReason, setSuspendReason] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    api
      .tenant(id)
      .then((result) => {
        setData(result)
        setError(null)
      })
      .catch((failure) => {
        onExpired(failure)
        setError(failure.message)
      })
  }, [id, onExpired])

  useEffect(load, [load])

  const when = (value) =>
    value ? new Date(value).toLocaleString(i18n.language, { dateStyle: 'short', timeStyle: 'short' }) : '—'

  async function adjust(event) {
    event.preventDefault()
    setBusy(true)
    setNotice(null)
    setError(null)

    try {
      const result = await api.adjustCredits(id, Number(qty), reason)
      setNotice(t('tenant.adjusted', { balance: result.balance }))
      setQty('')
      setReason('')
      load()
    } catch (failure) {
      onExpired(failure)
      setError(failure.message)
    } finally {
      setBusy(false)
    }
  }

  async function reingest(siteId) {
    setBusy(true)
    setNotice(null)

    try {
      await api.reingest(id, siteId)
      setNotice(t('tenant.reingested'))
    } catch (failure) {
      onExpired(failure)
      setError(failure.message)
    } finally {
      setBusy(false)
    }
  }

  if (!data) return <p className="muted center">{error ?? t('common.loading')}</p>

  return (
    <>
      <p>
        <a href="#/tenants">{t('tenant.back')}</a>
      </p>

      <section className="card">
        <h2>{data.name}</h2>
        <p className="muted small">
          #{data.id} · {data.locale} · {data.timezoneId} · {data.industry ?? '—'} ·{' '}
          {t('tenant.balanceNow', { balance: data.balance })}
        </p>

        {data.suspendedAt && <p className="status bad">{t('tenant.suspended', { reason: data.suspendedReason })}</p>}

        <div className="stats">
          <div className="stat">
            <div className="stat__value">{data.costMkd.toFixed(2)} ден</div>
            <div className="stat__label">{t('tenant.costMkd')}</div>
          </div>
          <div className="stat">
            <div className="stat__value">{Math.round(data.paidMkd)} ден</div>
            <div className="stat__label">{t('tenant.paidMkd')}</div>
          </div>
          <div className="stat">
            <div className="stat__value">{data.publishedCount}</div>
            <div className="stat__label">{t('tenant.publishedCount')}</div>
          </div>
        </div>

        {error && <p className="status bad">{error}</p>}
        {notice && <p className="status good">{notice}</p>}

        <h3>{t('tenant.usersTitle')}</h3>
        <ul className="plain">
          {data.users.map((user) => (
            <li key={user.id}>
              {user.displayName ?? user.email ?? user.fbUserId} · {user.role}
              {user.email && ` · ${user.email} (${t(user.emailVerified ? 'tenant.verified' : 'tenant.unverified')})`}
              {' · '}
              {t('tenant.lastLogin')}: {user.lastLoginAt ? when(user.lastLoginAt) : t('tenant.never')}
            </li>
          ))}
        </ul>
      </section>

      {data.wanted?.length > 0 && (
        <section className="card">
          <h3>{t('tenant.wantedTitle')}</h3>
          {data.wanted.map((purchase) => (
            <p key={purchase.id} className="row small">
              <span>
                {purchase.plan} · {purchase.credits} · {Math.round(purchase.amountMkd)} ден
              </span>
              <span className="muted">{when(purchase.createdAt)}</span>
            </p>
          ))}
          <p className="muted small">{t('tenant.wantedHint')}</p>
        </section>
      )}

      <section className="card">
        <h3>{t('tenant.suspendTitle')}</h3>
        <form
          className="row"
          onSubmit={(event) => {
            event.preventDefault()
            const on = !data.suspendedAt
            setBusy(true)
            api
              .suspend(id, on, suspendReason)
              .then(() => {
                setSuspendReason('')
                load()
              })
              .catch((failure) => {
                onExpired(failure)
                setError(failure.message)
              })
              .finally(() => setBusy(false))
          }}
        >
          <input
            required
            minLength={5}
            className="grow"
            placeholder={t('tenant.adjustReason')}
            value={suspendReason}
            onChange={(event) => setSuspendReason(event.target.value)}
          />
          <button
            type="submit"
            className={data.suspendedAt ? 'button' : 'button danger'}
            disabled={busy || suspendReason.length < 5}
          >
            {t(data.suspendedAt ? 'tenant.release' : 'tenant.suspend')}
          </button>
        </form>
      </section>

      <section className="card">
        <h3>{t('tenant.sitesTitle')}</h3>
        {data.sites.map((site) => (
          <p key={site.id} className="row">
            <span>
              {site.url} · {site.status} · {t('tenant.productsCount', { count: site.products })}
              {site.lastError && <span className="bad"> · {site.lastError}</span>}
            </span>
            <button type="button" className="button quiet" disabled={busy} onClick={() => reingest(site.id)}>
              {t('tenant.reingest')}
            </button>
          </p>
        ))}
      </section>

      <section className="card">
        <h3>{t('tenant.adjustTitle')}</h3>
        <form className="row" onSubmit={adjust}>
          <input
            type="number"
            required
            placeholder={t('tenant.adjustQty')}
            value={qty}
            onChange={(event) => setQty(event.target.value)}
          />
          <input
            type="text"
            required
            minLength={5}
            className="grow"
            placeholder={t('tenant.adjustReason')}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <button type="submit" className="button" disabled={busy || !qty || reason.length < 5}>
            {t('tenant.adjustDo')}
          </button>
        </form>

        <h3>
          {t('tenant.ledgerTitle')} <span className="muted small">{data.ledger.length}</span>
        </h3>
        <div className="scroll pane">
          <table>
            <tbody>
              {data.ledger.map((entry) => (
                <tr key={entry.id}>
                  <td className="small">{when(entry.createdAt)}</td>
                  <td>{entry.type}</td>
                  <td className="num">{entry.qty > 0 ? `+${entry.qty}` : entry.qty}</td>
                  <td className="num">{entry.balance}</td>
                  <td className="small muted">{entry.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card">
        <h3>
          {t('tenant.postsTitle')} <span className="muted small">{data.posts.length}</span>
        </h3>
        <div className="scroll pane">
          <table>
            <tbody>
              {data.posts.map((post) => (
                <tr key={post.id}>
                  <td>#{post.id}</td>
                  <td>{post.status}</td>
                  <td className="small">{when(post.publishedAt ?? post.scheduledAt ?? post.plannedFor ?? post.createdAt)}</td>
                  <td className="small clip">{post.text}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card">
        <h3>
          {t('tenant.runsTitle')} <span className="muted small">{data.runs.length}</span>
        </h3>
        <div className="scroll pane">
          <table>
            <tbody>
              {data.runs.map((run) => (
                <tr key={run.id} className={run.success ? '' : 'bad'}>
                  <td className="small">{when(run.createdAt)}</td>
                  <td>{run.purpose}</td>
                  <td className="small">{run.model}</td>
                  <td>{run.success ? t('tenant.ok') : t('tenant.failed')}</td>
                  <td className="num small">
                    {run.tokensIn}+{run.tokensOut}
                  </td>
                  <td className="num small">${(run.costMicros / 1_000_000).toFixed(4)}</td>
                  <td className="small muted clip">{run.error}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card">
        <h3>
          {t('tenant.chatTitle')} <span className="muted small">{data.chat.length}</span>
        </h3>
        <div className="chatlog pane">
          {data.chat.map((turn) => (
            <p key={turn.id} className={turn.fromOwner ? 'owner' : 'assistant'}>
              <span className="small muted">{when(turn.createdAt)}</span> {turn.text}
            </p>
          ))}
        </div>
      </section>

      <section className="card">
        <h3>
          {t('tenant.actionsTitle')} <span className="muted small">{data.actions.length}</span>
        </h3>
        <div className="pane">
        {data.actions.map((action) => (
          <p key={action.id} className="small">
            {when(action.createdAt)} · {action.admin} · <strong>{action.action}</strong> · {action.reason}
            {action.details && <span className="muted"> · {action.details}</span>}
          </p>
        ))}
        </div>
      </section>
    </>
  )
}
